import React from "react";
import ErrorFallback from "./component/ErrorFallback";

// A lazy-loaded chunk (e.g. the Inbox route) is fetched by a build-hashed
// filename. If the tab has been open since before the last deploy, that
// filename no longer exists and the server answers 404. This is a
// stale-deployment artifact, not a real app error, so reload once — index.html
// is served no-cache, so the reload picks up the current chunk names. The
// sessionStorage guard stops an infinite reload loop if the failure is
// actually a persistent network/build issue.
const CHUNK_ERROR_PATTERN = /Failed to fetch dynamically imported module|Loading chunk .* failed|Importing a module script failed|error loading dynamically imported module/i;
const CHUNK_RELOAD_GUARD_KEY = "chunk-load-reload-attempted";
// The guard records *when* we last reloaded rather than just that we did. A
// genuine loop retries within a second or two and stays blocked; a second
// deploy later in the same long-lived tab gets its one reload instead of a
// crash screen, which is what a plain once-per-session flag denied it.
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

const isChunkLoadError = (error) => CHUNK_ERROR_PATTERN.test(String(error?.message || ""));

const mayReloadForChunkError = () => {
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY));
  return !last || Date.now() - last > CHUNK_RELOAD_COOLDOWN_MS;
};

/**
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the app.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    // State to track whether an error has occurred
    this.state = {
      hasError: false,
      error: "",
    };
  }

  /**
   * Lifecycle method triggered when a child component throws an error
   * Used to update state and render fallback UI
   */
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  /**
   * Lifecycle method for logging error details
   * Useful for debugging and integrating error tracking tools
   */
  componentDidCatch(error, info) {
    if (isChunkLoadError(error) && mayReloadForChunkError()) {
      sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
      window.location.reload();
      return;
    }

    console.error("App Crash:", error, info);

    // Create Error Message with Error stack
    const simplifiedStack = info.componentStack
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => line.replace(/^in\s+/, ""))
      .splice(0, 3)
      .filter(Boolean);

    const finalError = (error?.message ? `${error?.message}\n` : "") + simplifiedStack.join("\n");
    this.setState({ error: finalError });

    // TODO:
    // Integrate external logging service like Sentry, LogRocket, etc.
  }

  render() {
    // If error occurred, show fallback UI
    if (this.state.hasError) {
      return <ErrorFallback errorType="INTERNAL" errorMessage={this.state.error} />;
    }

    // Otherwise render child components normally
    return this.props.children;
  }
}

export default ErrorBoundary;
